import React, { useState, useEffect, useCallback } from 'react';
import ConversationManager from './ConversationManager';

interface ChecklistItem {
  id: string;
  task: string;
  completed: boolean;
}

interface CheckIn {
  id: string;
  timestamp: Date;
  thoughts: string;
  checklist: ChecklistItem[];
  aiResponse?: string;
}

// Window.claude is now defined in types.ts

const CEO_CFO_QUESTIONS = [
  "What else could you be doing right now to move the needle?",
  "Who could you call that might unlock new opportunities?",
  "What numbers do we need to check today?", 
  "What bills or payments need attention?",
  "What deals are in the pipeline that need follow-up?",
  "What metrics should we be tracking this week?",
  "What partnerships or relationships need nurturing?",
  "What systems or processes are bottlenecking growth?",
  "What customer feedback are we ignoring?",
  "What competitive intelligence do we need?",
  "What cash flow projections need updating?",
  "What key decisions are you avoiding?",
  "What would your board ask you about today?",
  "What operational inefficiencies are costing us?",
  "What team member needs coaching or support?"
];

export function OrkWayApp() {
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
  const [thoughts, setThoughts] = useState<string>('');
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [conversationSession, setConversationSession] = useState<{
    question: string;
    response: string;
    sessionId: string;
  } | null>(null);
  const [nextCheckIn, setNextCheckIn] = useState<Date>(new Date(Date.now() + 60 * 60 * 1000));

  const generateQuestion = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * CEO_CFO_QUESTIONS.length);
    setCurrentQuestion(CEO_CFO_QUESTIONS[randomIndex]);
  }, []);

  const generateChecklist = useCallback(async () => {
    if (!thoughts.trim()) {
      return;
    }

    setIsGenerating(true);
    try {
      console.log('🚀 Calling AI API with:', { question: currentQuestion, response: thoughts });
      
      const response = await fetch('/api/ai/generate-actions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: currentQuestion,
          response: thoughts
        })
      });

      console.log('📡 API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✨ AI Response data:', data);
      
      if (data.success && data.actionItems && data.actionItems.length > 0) {
        const tasks = data.actionItems.map((task: string, index: number) => ({
          id: `task-${Date.now()}-${index}`,
          task,
          completed: false
        }));
        setChecklist(tasks);
        console.log('✅ Checklist updated with AI tasks:', tasks.length);
        
        // Create conversation session
        const sessionId = `session-${Date.now()}`;
        const session = {
          question: currentQuestion,
          response: thoughts,
          sessionId
        };
        console.log('🎯 Creating conversation session:', session);
        setConversationSession(session);
        setShowConversation(true);
        console.log('🔄 Show conversation set to:', true);
      } else {
        console.warn('⚠️ No action items in response:', data);
        throw new Error('No action items generated');
      }
    } catch (error) {
      console.error('❌ Failed to generate checklist:', error);
      // Fallback to manual checklist creation
      const defaultTasks = [
        'Review daily revenue numbers',
        'Check cash flow status', 
        'Follow up on pending deals',
        'Address urgent team needs',
        'Plan tomorrow\'s priorities'
      ].map((task, index) => ({
        id: `fallback-${Date.now()}-${index}`,
        task,
        completed: false
      }));
      setChecklist(defaultTasks);
    } finally {
      setIsGenerating(false);
    }
  }, [thoughts, currentQuestion]);

  const toggleChecklistItem = (id: string) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const saveCheckIn = useCallback(() => {
    const checkIn: CheckIn = {
      id: `checkin-${Date.now()}`,
      timestamp: new Date(),
      thoughts,
      checklist: [...checklist]
    };

    const updatedCheckIns = [checkIn, ...checkIns];
    setCheckIns(updatedCheckIns);
    
    // Save to localStorage
    try {
      localStorage.setItem('orkway-checkins', JSON.stringify(updatedCheckIns));
      console.log('💾 Saved check-in to localStorage');
    } catch (error) {
      console.error('Failed to save check-in to localStorage:', error);
    }
    
    setThoughts('');
    setChecklist([]);
    setNextCheckIn(new Date(Date.now() + 60 * 60 * 1000));
    generateQuestion();
  }, [thoughts, checklist, checkIns, generateQuestion]);

  useEffect(() => {
    generateQuestion();
  }, [generateQuestion]);

  // Load check-ins from localStorage on component mount
  useEffect(() => {
    const savedCheckIns = localStorage.getItem('orkway-checkins');
    if (savedCheckIns) {
      try {
        const parsed = JSON.parse(savedCheckIns);
        const checkInsWithDates = parsed.map((checkIn: any) => ({
          ...checkIn,
          timestamp: new Date(checkIn.timestamp)
        }));
        setCheckIns(checkInsWithDates);
        console.log('📥 Loaded check-ins from localStorage:', checkInsWithDates.length);
      } catch (error) {
        console.error('Failed to load check-ins from localStorage:', error);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (Date.now() >= nextCheckIn.getTime()) {
        generateQuestion();
        setNextCheckIn(new Date(Date.now() + 60 * 60 * 1000));
      }
    }, 60000); // Check every minute

    return () => clearInterval(timer);
  }, [nextCheckIn, generateQuestion]);

  const timeUntilNext = Math.max(0, nextCheckIn.getTime() - Date.now());
  const minutesUntilNext = Math.floor(timeUntilNext / (1000 * 60));

  // Debug logging for modal state
  console.log('🔍 Modal state:', { showConversation, conversationSession });

  return (
    <>
      {showConversation && conversationSession && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75">
          <div className="h-full bg-white">
            <div className="p-4 bg-purple-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">💬 Accountability Session</h2>
              <button
                onClick={() => setShowConversation(false)}
                className="text-white hover:bg-white/20 p-2 rounded"
              >
                ✕ Close
              </button>
            </div>
            <ConversationManager 
              initialQuestion={conversationSession.question}
              initialResponse={conversationSession.response}
              onSessionComplete={() => {
                setShowConversation(false);
                setConversationSession(null);
              }}
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">orkWay</h1>
          <p className="text-xl opacity-80">Your AI Accountability Partner</p>
          <div className="mt-4 text-sm opacity-60">
            Next check-in in: {Math.floor(minutesUntilNext / 60)}h {minutesUntilNext % 60}m
          </div>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Current Question & Input */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h2 className="text-2xl font-semibold mb-4">💡 Current Question</h2>
            <div className="bg-white/5 rounded-lg p-4 mb-6">
              <p className="text-lg leading-relaxed">{currentQuestion}</p>
            </div>
            
            <textarea
              value={thoughts}
              onChange={(e) => setThoughts(e.target.value)}
              placeholder="Share your thoughts, observations, ideas..."
              className="w-full h-32 p-4 bg-white/10 border border-white/20 rounded-lg resize-none placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={generateChecklist}
                disabled={!thoughts.trim() || isGenerating}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                {isGenerating ? 'Generating...' : '🎯 Create Action Items'}
              </button>
              <button
                onClick={generateQuestion}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                🔄
              </button>
            </div>
          </div>

          {/* Action Checklist */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h2 className="text-2xl font-semibold mb-4">✅ Action Items</h2>
            {checklist.length === 0 ? (
              <div className="text-center py-8 opacity-60">
                <p>Share your thoughts first,</p>
                <p>then generate action items</p>
              </div>
            ) : (
              <div className="space-y-3">
                {checklist.map(item => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      item.completed ? 'bg-green-500/20 opacity-60' : 'bg-white/5 hover:bg-white/10'
                    }`}
                    onClick={() => toggleChecklistItem(item.id)}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 ${
                      item.completed 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-white/30'
                    }`}>
                      {item.completed && (
                        <svg className="w-3 h-3 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className={item.completed ? 'line-through' : ''}>
                      {item.task}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {(thoughts.trim() || checklist.length > 0) && (
              <button
                onClick={saveCheckIn}
                className="w-full mt-6 bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg font-semibold transition-colors"
              >
                💾 Save Check-In
              </button>
            )}
          </div>
        </div>

        {/* Previous Check-ins */}
        {checkIns.length > 0 && (
          <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h2 className="text-2xl font-semibold mb-4">📋 Previous Check-ins</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {checkIns.slice(0, 5).map(checkIn => (
                <div key={checkIn.id} className="bg-white/5 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm opacity-60">
                      {checkIn.timestamp.toLocaleString()}
                    </span>
                    <span className="text-sm opacity-60">
                      {checkIn.checklist.filter(item => item.completed).length}/{checkIn.checklist.length} completed
                    </span>
                  </div>
                  <p className="mb-2 opacity-80">{checkIn.thoughts}</p>
                  <div className="text-sm opacity-60">
                    {checkIn.checklist.length} action items generated
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}